import { NgIf } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	NgZone,
	OnDestroy,
	OnInit
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { AiSummaryDetailResponse } from '@brand-lolly/app/social-media-influencer/modules/social-listening/models/social-listening-ai-summary.model';
import { AiSummaryContentComponent } from '@brand-lolly/app/social-media-influencer/modules/social-listening/pages/ai-summary/components/ai-summary-content.component';
import { SocialListeningAiSummaryService } from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/social-listening-ai-summary.service';
import { TypographyComponent } from 'components';

const EXPORT_TIMEOUT_MS = 15000;

@Component({
	standalone: true,
	selector: 'app-ai-summary-export',
	templateUrl: './ai-summary-export.component.html',
	styleUrls: ['./ai-summary-export.component.scss'],
	imports: [NgIf, AiSummaryContentComponent, TypographyComponent]
})
export class AiSummaryExportComponent
	implements OnInit, AfterViewInit, OnDestroy
{
	summary: AiSummaryDetailResponse | null = null;
	errorMessage = '';

	private readonly contentReady$ = new BehaviorSubject<boolean>(false);
	private readonly destroy$ = new Subject<void>();

	constructor(
		private route: ActivatedRoute,
		private aiSummaryService: SocialListeningAiSummaryService,
		private zone: NgZone,
		private cdRef: ChangeDetectorRef
	) {}

	ngOnInit(): void {
		(window as any).aiSummaryExportReady = false;
		document.body.classList.add('ai-summary-export-mode');

		setTimeout(() => this.markReady('timeout'), EXPORT_TIMEOUT_MS);

		const summaryId = this.route.snapshot.paramMap.get('summaryId') ?? '';
		const exportToken =
			this.route.snapshot.queryParamMap.get('exportToken') ?? '';

		if (!summaryId || !exportToken) {
			this.markError('Missing export parameters.');
			return;
		}

		this.aiSummaryService
			.getForExport(summaryId, exportToken)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: detail => {
					this.summary = detail;
					this.cdRef.detectChanges();
					this.zone.runOutsideAngular(() => {
						Promise.all([this.waitForFonts(), this.waitForImages()])
							.then(() => this.wait(600))
							.then(() => this.waitForMaskImages())
							.then(() => this.wait(600))
							.then(() => {
								this.layoutPages();
								return this.wait(200);
							})
							.then(() => {
								console.log(
									'[ai-summary-export] content-ready'
								);
								this.contentReady$.next(true);
							});
					});
				},
				error: err => {
					console.error('[ai-summary-export] load failed', err);
					this.markError('Failed to load summary data.');
				}
			});

		this.contentReady$
			.pipe(
				filter(ready => ready),
				take(1),
				takeUntil(this.destroy$)
			)
			.subscribe(() => this.markReady('all-ready'));
	}

	ngAfterViewInit(): void {
		this.cdRef.detach();
	}

	ngOnDestroy(): void {
		document.body.classList.remove('ai-summary-export-mode');
		this.destroy$.next();
		this.destroy$.complete();
	}

	private waitForFonts(): Promise<void> {
		if (!document.fonts?.load) return Promise.resolve();
		const weights = [400, 500, 600, 700, 800, 900];
		return Promise.race([
			Promise.all(
				weights.map(w =>
					document.fonts
						.load(`${w} 1em Gilroy`)
						.catch(() => undefined)
				)
			).then(() => undefined),
			this.wait(4000)
		]);
	}

	private waitForImages(): Promise<void> {
		const imgs = Array.from(
			document.querySelectorAll<HTMLImageElement>('img')
		);
		const pending = imgs.filter(img => !img.complete);
		if (!pending.length) return Promise.resolve();

		return Promise.race([
			Promise.all(
				pending.map(
					img =>
						new Promise<void>(resolve => {
							img.addEventListener('load', () => resolve(), {
								once: true
							});
							img.addEventListener('error', () => resolve(), {
								once: true
							});
						})
				)
			).then(() => undefined),
			this.wait(4000)
		]);
	}

	private waitForMaskImages(): Promise<void> {
		const urls = new Set<string>();
		document
			.querySelectorAll<HTMLElement>('[style*="url("]')
			.forEach(el => {
				const style = el.getAttribute('style') ?? '';
				for (const match of style.matchAll(
					/url\(['"]?([^'")\s]+)['"]?\)/g
				)) {
					urls.add(match[1]);
				}
			});
		if (!urls.size) return Promise.resolve();
		return Promise.race([
			Promise.all(
				Array.from(urls).map(
					url =>
						new Promise<void>(resolve => {
							const img = new Image();
							img.onload = () => resolve();
							img.onerror = () => resolve();
							img.src = url;
						})
				)
			).then(() => undefined),
			this.wait(3000)
		]);
	}

	private layoutPages(): void {
		// A4 at 96dpi = 1122px minus 2rem top + 2rem bottom @page margins
		const PAGE_H = 1058;
		// Gap between sections within a page (matches :host gap: 2rem)
		const PAGE_GAP = 32;
		// Gap between items inside a .content-section (matches gap: 1rem)
		const INNER_GAP = 16;

		const host = document.querySelector<HTMLElement>(
			'app-ai-summary-content'
		);
		if (!host) return;

		// Title is above app-ai-summary-content; its height consumes page 1 space
		const titleEl = document.querySelector<HTMLElement>('.export-title');
		const initialUsed = titleEl
			? titleEl.getBoundingClientRect().height + PAGE_GAP
			: 0;

		// Snapshot sections before any DOM moves
		const sections = Array.from(host.children) as HTMLElement[];

		// --- Bin-packing state ---
		const pages: HTMLElement[][] = [[]];
		let usedH = initialUsed;

		const remaining = (): number => PAGE_H - usedH;

		const fits = (height: number): boolean => {
			const gap = pages[pages.length - 1].length > 0 ? PAGE_GAP : 0;
			return remaining() >= gap + height;
		};

		const addToPage = (el: HTMLElement, height: number): void => {
			const gap = pages[pages.length - 1].length > 0 ? PAGE_GAP : 0;
			pages[pages.length - 1].push(el);
			usedH += gap + height;
		};

		const nextPage = (): void => {
			pages.push([]);
			usedH = 0;
		};

		// --- Process each section ---
		for (const section of sections) {
			// Signal cards are direct children — they can be split across pages
			const splitItems = Array.from(
				section.querySelectorAll<HTMLElement>(
					':scope > app-ai-summary-signal-card'
				)
			);

			if (!splitItems.length) {
				// Non-splittable: move whole section
				let h = section.getBoundingClientRect().height;

				// If section doesn't fit and has voice cards, try clamping them
				// to the remaining space so the section stays on the current page
				if (!fits(h) && usedH > 0) {
					const pageGap =
						pages[pages.length - 1].length > 0 ? PAGE_GAP : 0;
					const availForSection = remaining() - pageGap;
					if (
						section.querySelector('.standout-voices') &&
						this.tryClampVoiceCards(section, availForSection)
					) {
						h = section.getBoundingClientRect().height;
					}
				}

				if (!fits(h) && usedH > 0) {
					nextPage();
				}
				addToPage(section, h);
				continue;
			}

			// Splittable signals section
			// Header = all non-signal children (just the title for signals)
			const headerEls = Array.from(section.children).filter(
				(c): c is HTMLElement =>
					c.tagName.toLowerCase() !== 'app-ai-summary-signal-card'
			);

			// Measure header height: sum of header children + gaps between them
			const headerH =
				headerEls.reduce(
					(s, el) => s + el.getBoundingClientRect().height,
					0
				) +
				Math.max(0, headerEls.length - 1) * INNER_GAP;

			// Measure every card before touching DOM
			const cardHeights = splitItems.map(
				c => c.getBoundingClientRect().height
			);

			let cardIdx = 0;
			let isFirstBatch = true;

			while (cardIdx < splitItems.length) {
				const batchHeaderH = isFirstBatch ? headerH : 0;
				const firstCardH = cardHeights[cardIdx];
				const gapAfterHeader = batchHeaderH > 0 ? INNER_GAP : 0;

				// Ensure header + first card fits; if not, start new page
				if (
					!fits(batchHeaderH + gapAfterHeader + firstCardH) &&
					usedH > 0
				) {
					nextPage();
				}

				const wrap = document.createElement('section');
				wrap.className = 'content-section';
				wrap.style.display = 'flex';
				wrap.style.flexDirection = 'column';
				wrap.style.gap = INNER_GAP + 'px';

				if (isFirstBatch) {
					headerEls.forEach(el => wrap.appendChild(el));
					isFirstBatch = false;
				}

				const pageGap =
					pages[pages.length - 1].length > 0 ? PAGE_GAP : 0;
				let wrapH = batchHeaderH;
				let cardsInBatch = 0;

				while (cardIdx < splitItems.length) {
					const gapBeforeCard = wrapH > 0 ? INNER_GAP : 0;
					const newWrapH =
						wrapH + gapBeforeCard + cardHeights[cardIdx];

					if (pageGap + newWrapH > remaining() && cardsInBatch > 0) {
						break;
					}

					wrap.appendChild(splitItems[cardIdx]);
					wrapH = newWrapH;
					cardIdx++;
					cardsInBatch++;
				}

				addToPage(wrap, wrapH);

				if (cardIdx < splitItems.length) {
					nextPage();
				}
			}
		}

		// --- Build page divs and replace host content ---
		while (host.firstChild) host.removeChild(host.firstChild);

		pages.forEach((pageSections, i) => {
			const pageDiv = document.createElement('div');
			pageDiv.className = 'pdf-page';
			pageDiv.style.display = 'flex';
			pageDiv.style.flexDirection = 'column';
			pageDiv.style.gap = PAGE_GAP + 'px';
			if (i < pages.length - 1) {
				pageDiv.style.breakAfter = 'page';
			}
			pageSections.forEach(el => pageDiv.appendChild(el));
			host.appendChild(pageDiv);
		});
	}

	/**
	 * Clamps voice card body text so the section fits within availH pixels.
	 * Returns true if clamping was applied, false if space is too small to bother.
	 */
	private tryClampVoiceCards(section: HTMLElement, availH: number): boolean {
		const SAFE_BUFFER = 24;

		const standoutVoices =
			section.querySelector<HTMLElement>('.standout-voices');
		if (!standoutVoices) return false;

		const sectionTop = section.getBoundingClientRect().top;
		const cards = Array.from(
			standoutVoices.querySelectorAll<HTMLElement>('.voice-card')
		);
		if (!cards.length) return false;

		let minLines = Infinity;

		cards.forEach(card => {
			const bodyEl = card.querySelector<HTMLElement>('.voice-card__body');
			const footerEl = card.querySelector<HTMLElement>(
				'.voice-card__footer'
			);
			if (!bodyEl || !footerEl) return;

			const bodyTop = bodyEl.getBoundingClientRect().top;
			const footerH = footerEl.getBoundingClientRect().height;
			const cardPaddingBottom =
				card.getBoundingClientRect().bottom -
				footerEl.getBoundingClientRect().bottom;

			const available =
				availH -
				(bodyTop - sectionTop) -
				footerH -
				cardPaddingBottom -
				SAFE_BUFFER;

			const lineH = parseFloat(getComputedStyle(bodyEl).lineHeight) || 24;
			const maxLines = Math.max(1, Math.floor(available / lineH));
			minLines = Math.min(minLines, maxLines);
		});

		if (!isFinite(minLines) || minLines < 3) return false;

		const lineH =
			parseFloat(
				getComputedStyle(
					cards[0].querySelector<HTMLElement>('.voice-card__body')!
				).lineHeight
			) || 24;
		const bodyMaxH = minLines * lineH;

		cards.forEach(card => {
			const bodyEl = card.querySelector<HTMLElement>('.voice-card__body');
			if (!bodyEl) return;
			// flex: 1 shorthand sets flex-basis: 0% — override the whole shorthand
			// so flex-basis becomes auto and the element gets its natural height.
			bodyEl.style.flex = 'none';
			bodyEl.style.maxHeight = bodyMaxH + 'px';
			bodyEl.style.display = '-webkit-box';
			bodyEl.style.webkitBoxOrient = 'vertical';
			bodyEl.style.webkitLineClamp = String(minLines);
			bodyEl.style.overflow = 'hidden';
		});

		return true;
	}

	private wait(ms: number): Promise<void> {
		return new Promise(resolve => setTimeout(resolve, ms));
	}

	private markError(message: string): void {
		this.errorMessage = message;
		console.error('[ai-summary-export] error:', message);
		this.markReady('error');
		this.cdRef.detectChanges();
	}

	private markReady(reason: string): void {
		const win = window as any;
		if (win.aiSummaryExportReady === true) return;
		win.aiSummaryExportReady = true;
		console.log('[ai-summary-export] ready=true reason=%s', reason);
	}
}
